import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { TermsModal, PWAInstallButton } from '../components/ui';

export const Login: React.FC = () => {
  
  const [email, setEmail] = useState('');
  const [activationCode, setActivationCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    // Controlla se le condizioni sono accettate
    if (!acceptTerms) {
      setError('Devi accettare le condizioni di utilizzo per procedere');
      return;
    }
    
    // Procede direttamente con il login
    setIsLoading(true);

    try {
      await login(email, activationCode);
      navigate('/');
    } catch (err: any) {
      console.error('❌ [LOGIN FORM] Errore nel login:', err);
      // Gestione specifica degli errori
      if (err.response?.status === 403) {
        setError(err.response?.data?.message || 'Accesso negato: utente bloccato');
      } else if (err.response?.status === 401) {
        // Gestione specifica per errori 401 (codice non valido, inattivo, scaduto)
        const message = err.response?.data?.message || 'Credenziali non valide o codice di attivazione errato';
        if (message.includes('non attivo')) {
          setError('Codice di attivazione non attivo. Contatta l\'amministratore.');
        } else if (message.includes('scaduto')) {
          setError('Codice di attivazione scaduto. Richiedi un nuovo codice.');
        } else if (message.includes('non valido')) {
          setError('Codice di attivazione non valido. Verifica il codice inserito.');
        } else if (message.includes('già utilizzato da un altro utente')) {
          setError('Codice di attivazione già utilizzato da un altro utente. Contatta l\'amministratore.');
        } else if (message.includes('Limite dispositivi raggiunto')) {
          setError('Limite dispositivi raggiunto per questo codice. Contatta l\'amministratore.');
        } else {
          setError(message);
        }
      } else if (err.response?.status === 400) {
        // Gestione specifica per errori 400 (codice inattivo, scaduto, ecc.)
        const message = err.response?.data?.message || 'Errore con il codice di attivazione';
        if (message.includes('non attivo')) {
          setError('Codice di attivazione non attivo. Contatta l\'amministratore.');
        } else if (message.includes('scaduto')) {
          setError('Codice di attivazione scaduto. Richiedi un nuovo codice.');
        } else if (message.includes('Limite dispositivi')) {
          setError('Limite dispositivi raggiunto per questo codice.');
        } else if (message.includes('Limite tentativi')) {
          setError('Limite tentativi raggiunto per questo codice.');
        } else {
          setError(message);
        }
      } else if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Errore durante il login. Riprova più tardi.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-gray-50 scrollable-page">
      <div className="flex flex-col min-h-screen">
        <div className="flex flex-col justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            {/* Logo */}
            <div className="flex justify-center">
              <Link to="/" className="flex items-center space-x-2">
                <img 
                  src="/logo-live-concorsi.png" 
                  alt="Live Concorsi" 
                  className="h-20 sm:h-24 md:h-28 lg:h-32 w-auto transition-all duration-300"
                />
                <span className="text-2xl sm:text-3xl font-bold text-gray-900">Live Concorsi</span>
              </Link>
            </div>

            <h2 className="mt-4 sm:mt-6 text-center text-2xl sm:text-3xl font-bold text-gray-900">
              Accedi al tuo account
            </h2>
            <p className="mt-3 sm:mt-4 text-center text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
              Preparati ai concorsi con quiz interattivi, simulazioni realistiche e 
              aggiornamenti in tempo reale. Inizia subito il tuo percorso di studio!
            </p>
            <p className="mt-2 text-center text-sm text-gray-600">
              Non hai un account?{' '}
              <Link
                to="/register"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                Registrati qui
              </Link>
            </p>
          </div>

          <div className="mt-6 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-6 sm:py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <form className="space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Indirizzo email
                  </label>
                  <div className="mt-1">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input-field"
                      placeholder="inserisci@email.com"
                    />
                  </div>
                </div>

                {/* Activation Code */}
                <div>
                  <label htmlFor="activationCode" className="block text-sm font-medium text-gray-700">
                    Codice di Attivazione
                  </label>
                  <div className="mt-1 relative">
                    <input
                      id="activationCode"
                      name="activationCode"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="off"
                      required
                      value={activationCode}
                      onChange={(e) => setActivationCode(e.target.value)}
                      className="input-field pr-10"
                      placeholder="Inserisci il tuo codice di attivazione"
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? (
                        <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                      ) : (
                        <EyeIcon className="h-5 w-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className={`rounded-lg p-3 sm:p-4 ${
                    error.includes('bloccato') || error.includes('Accesso negato')
                      ? 'bg-red-50 border border-red-200'
                      : error.includes('non attivo') || error.includes('scaduto') || error.includes('Limite')
                      ? 'bg-orange-50 border border-orange-200'
                      : 'bg-danger-50 border border-danger-200'
                  }`}>
                    <div className="flex">
                      <div className="flex-shrink-0">
                        {error.includes('bloccato') || error.includes('Accesso negato') ? (
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        ) : error.includes('non attivo') || error.includes('scaduto') || error.includes('Limite') ? (
                          <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-danger-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="ml-3">
                        <p className={`text-sm font-medium ${
                          error.includes('bloccato') || error.includes('Accesso negato')
                            ? 'text-red-800'
                            : error.includes('non attivo') || error.includes('scaduto') || error.includes('Limite')
                            ? 'text-orange-800'
                            : 'text-danger-700'
                        }`}>
                          {error}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Terms Acceptance Checkbox */}
                <div className="flex items-start">
                  <div className="flex items-center h-5">
                    <input
                      id="accept-terms"
                      name="accept-terms"
                      type="checkbox"
                      checked={acceptTerms}
                      onChange={(e) => setAcceptTerms(e.target.checked)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                  </div>
                  <div className="ml-3 text-sm">
                    <label htmlFor="accept-terms" className="text-gray-700">
                      Accetto le{' '}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-primary-600 hover:text-primary-500 underline font-medium"
                      >
                        condizioni di utilizzo
                      </button>
                      {' '}e la{' '}
                      <button
                        type="button"
                        onClick={() => setShowTermsModal(true)}
                        className="text-primary-600 hover:text-primary-500 underline font-medium"
                      >
                        informativa sulla privacy
                      </button>
                    </label>
                  </div>
                </div>

                {/* Pulsante Accedi */}
                <div className="mt-6">
                  <button
                    type="submit"
                    disabled={isLoading || !acceptTerms}
                    onClick={(e) => {
                      e.preventDefault();
                      handleSubmit(e);
                    }}
                    className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed py-3 sm:py-4 text-base sm:text-lg font-medium"
                  >
                    {isLoading ? 'Accesso in corso...' : 'Accedi'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Footer con PWA Install Button */}
        <footer className="mt-8 py-4 border-t border-gray-200">
          <div className="flex justify-center">
            <PWAInstallButton 
              showOnMobileOnly={false}
              className="text-sm"
            />
          </div>
        </footer>
      </div>

      {/* Terms Modal - Solo per visualizzazione */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
      />
    </div>
  );
};
